/**
 * # dxfBridge.newRecordHandles — records CREATED in an imported drawing (export preparation)
 *
 * An IMPORTED document bypasses the from-scratch conformant skeleton on save
 * (`ensureConformantSkeleton` returns it verbatim so every pre-existing record
 * stays byte-faithful), yet the user keeps CREATING typed records in it — a
 * cloned DIMSTYLE, a new LAYER / STYLE / LTYPE, a BLOCK (definition + record +
 * cloned children), a LAYOUT tab, an INSERT with attributes, and every
 * authored entity (`geometry/create-line`, COPY, MIRROR, ARRAY, … — reducer
 * ids such as `line-1`). None of them carries a DXF handle (the store never
 * mints handles), so they were exported without their `5` / `105` (or with a
 * NON-hex `5 line-1`) and without owners; ezdxf silently minted / re-homed
 * them (no audit fix) and strict readers reject the file. R13+ requires every
 * record to own a hex handle and a `330` owner (Autodesk DXF reference;
 * `$HANDSEED` = "next available object handle").
 *
 * This pass produces a DERIVED document for the export bridge (the caller's
 * document is never mutated — export is a projection; two exports of the same
 * document are byte-identical because the mint is deterministic: traversal
 * order + the document's own handle maximum). Sections, in order:
 *
 *   1. census + minter — every handle the document carries
 *      ({@link collectDocumentHandlesDeep}) seeds a `bigint` minter starting
 *      above the document maximum and `$HANDSEED`; `claimed` tracks the own
 *      handles already emitted so a duplicate is reminted (first occurrence
 *      verbatim).
 *   2. table records — LAYER `5`, STYLE `5`, DIMSTYLE `105`, LTYPE `5` for a
 *      record lacking a valid hex handle; `330` = the captured table head
 *      (`symbolTableHeaders` / `linetypeTableHeader`), else a sibling's owner,
 *      else unset (never fabricated).
 *   3. blocks — a NEW BLOCK (non-hex handle) gets `5` + ENDBLK `5`, its
 *      same-named BLOCK_RECORD is created / minted, the BLOCK is owned by it
 *      and EVERY child is re-parented to it (the BLOCK dialog clones the
 *      selection verbatim, space owner included). A DIMENSION created after
 *      import first receives its anonymous `*D<n>` BLOCK + BLOCK_RECORD
 *      (`assignAnonymousDimensionBlocks`, the skeleton's own assignment) so
 *      they are minted, owned and counted like any new block.
 *   4. layouts — a LAYOUT created by LAYOUT NEW gets its `5`, its
 *      `*Paper_Space<N>` BLOCK_RECORD + BLOCK, the `330` ⇄ `340` pairing, the
 *      ACAD_LAYOUT dictionary owner and a `3/350` dictionary child
 *      (`dxfBridge.newRecordHandles.layouts.ts`, mirroring the skeleton's
 *      paper-space reconciliation).
 *   5. entities — every emitted top-level / block-child entity whose own id
 *      is not a hex handle is minted one; sibling references between them
 *      (ATTRIB `attDefHandle`, HATCH `sourceBoundaryHandles`, viewport
 *      layer-override carriers, MLEADER refs, `{code, handle}[]`, XData
 *      1005) follow through the skeleton's generic walker
 *      (`rewriteEntityHandlesGeneric` / `HANDLE_FIELD_CLASS`, never a hand
 *      list); a missing `330` is wired to the space's BLOCK_RECORD; an
 *      INSERT-with-ATTRIB gets fresh ATTRIB / SEQEND handles when absent or
 *      duplicated (a plain spread of an imported INSERT copies them).
 *
 * The pass is gated on the document owning a handle space (a HEADER
 * `$HANDSEED`): an R12-shaped source without one has no mandatory handles, and
 * stamping them would break its byte-faithful round-trip.
 *
 * Source of truth: Autodesk DXF reference (R2018) + ezdxf 1.4.3.
 */
import type { DxfGroup } from "@dxf-ai-editor/dxf-io";

import { isLayoutBlockName } from "./blockNaming";
import { collectHandlesDeep } from "./dxfBridge.annotativeExport";
import { collectRawOwnHandles } from "./dxfBridge.conformantSkeleton";
import {
  assignAnonymousDimensionBlocks,
  takeAnonymousDimensionBlockName,
  type AnonymousDimensionBlockAssignment,
} from "./dxfBridge.conformantSkeleton.dimBlocks";
import { emittedBlockEntries } from "./dxfBridge.conformantSkeleton.emittedEntities";
import {
  isHexHandle,
  rewriteEntityHandlesGeneric,
} from "./dxfBridge.conformantSkeleton.handles";
import { readTableHeadHandle } from "./dxfBridge.conformantSkeleton.tableOwners";
import {
  createPreparationContext,
  documentHandSeed,
  lacksOwnHandle,
  mintBlockRecord,
  type PreparationContext,
} from "./dxfBridge.newRecordHandles.context";
import { prepareImportedLayouts } from "./dxfBridge.newRecordHandles.layouts";
import {
  emitsTerminatingSeqend,
  ownsNestedRecordIdentity,
} from "./entityRecordIdentity";
import {
  blockRecordSchema,
  type BlockDefinition,
  type BlockRecord,
  type DrawingDocument,
} from "./model";

/**
 * Claim every own handle the export emits from records this pass does NOT
 * rewrite one by one: the raw group streams (first `5` / `105` per record).
 * Typed records claim their handles as each section visits them.
 */
function claimRawOwnHandles(document: DrawingDocument, ctx: PreparationContext): void {
  const own = new Set<string>();
  for (const raw of document.rawEntityGroups ?? []) collectRawOwnHandles(raw.groups, own);
  collectRawOwnHandles(document.rawObjectGroups ?? [], own);
  collectRawOwnHandles(document.rawTableGroups ?? [], own);
  for (const section of document.rawPassthroughSections ?? []) {
    // HEADER / CLASSES carry no objects; the HEADER's only `5` is the
    // `$HANDSEED` value (the next FREE handle), which must not be claimed.
    if (section.name === "HEADER" || section.name === "CLASSES") continue;
    collectRawOwnHandles(section.groups, own);
  }
  for (const h of own) ctx.claim(h);
}

/* ── 2. symbol-table records ─────────────────────────────────────────────── */

type OwnedRecord = { handle?: string; ownerHandle?: string };

/** Mint / own every record of one table lacking a valid handle; identity when none. */
function stampTableRecords<T extends OwnedRecord>(
  records: readonly T[],
  head: readonly DxfGroup[] | undefined,
  ctx: PreparationContext,
): T[] {
  const owner =
    readTableHeadHandle(head) ??
    records.find((r) => r.ownerHandle !== undefined && r.ownerHandle.length > 0)?.ownerHandle;
  let changed = false;
  const next = records.map((record) => {
    if (!lacksOwnHandle(record.handle)) {
      ctx.claim(record.handle!);
      return record;
    }
    changed = true;
    const stamped: T = { ...record, handle: ctx.mint() };
    if (stamped.ownerHandle === undefined && owner !== undefined) stamped.ownerHandle = owner;
    return stamped;
  });
  return changed ? next : (records as T[]);
}

function prepareStyleTables(document: DrawingDocument, ctx: PreparationContext): DrawingDocument["styleTables"] {
  const tables = document.styleTables;
  const heads = document.symbolTableHeaders ?? {};
  const layers = stampTableRecords(tables.layers, heads.LAYER, ctx);
  const textStyles = stampTableRecords(tables.textStyles, heads.STYLE, ctx);
  const dimensionStyles = stampTableRecords(tables.dimensionStyles, heads.DIMSTYLE, ctx);
  if (
    layers === tables.layers &&
    textStyles === tables.textStyles &&
    dimensionStyles === tables.dimensionStyles
  ) {
    return tables;
  }
  return { ...tables, layers, textStyles, dimensionStyles };
}

/**
 * LTYPE records — only the ones the export EMITS (`referencedLinetypeNames`,
 * the same reference-filtered set `exportStyleTables` applies): the importer
 * seeds every acad.lin linetype into `document.linetypes` WITHOUT a handle and
 * the export drops the unreferenced ones, so minting for all of them would
 * burn handle space for records that never reach the file.
 */
function prepareLinetypes(
  document: DrawingDocument,
  ctx: PreparationContext,
  referencedLinetypeNames: ReadonlySet<string> | undefined,
): DrawingDocument["linetypes"] {
  const linetypes = document.linetypes;
  if (!linetypes) return linetypes;
  const referenced =
    referencedLinetypeNames === undefined
      ? undefined
      : new Set([...referencedLinetypeNames].map((n) => n.toLowerCase()));
  const entries = Object.entries(linetypes).filter(
    ([name, lt]) => referenced === undefined || referenced.has(name.toLowerCase()) || referenced.has(lt.name.toLowerCase()),
  );
  const stamped = stampTableRecords(
    entries.map(([, lt]) => lt),
    document.linetypeTableHeader,
    ctx,
  );
  if (stamped.every((lt, i) => lt === entries[i]![1])) return linetypes;
  const next: NonNullable<DrawingDocument["linetypes"]> = { ...linetypes };
  entries.forEach(([name], i) => {
    next[name] = stamped[i]!;
  });
  return next;
}

/* ── 3. blocks + block records ───────────────────────────────────────────── */

/** Result of {@link prepareBlocks}: the derived maps + the names of NEW blocks. */
interface PreparedBlocks {
  blocks: DrawingDocument["blocks"];
  blockRecords: DrawingDocument["blockRecords"];
  /** Blocks whose own handle was minted here — their children are re-parented. */
  newBlockNames: Set<string>;
}

function prepareBlocks(document: DrawingDocument, ctx: PreparationContext): PreparedBlocks {
  const blockRecords: Record<string, BlockRecord> = { ...document.blockRecords };
  const blocks: Record<string, BlockDefinition> = { ...document.blocks };
  const newBlockNames = new Set<string>();
  let changed = false;

  // Records first: an existing imported record claims its handle; a record
  // lacking one (a BLOCK created after import) is minted and owned by the
  // BLOCK_RECORD table head (see `mintBlockRecord`).
  const ownRecord = (record: BlockRecord): BlockRecord => mintBlockRecord(document, record, ctx);
  for (const [name, record] of Object.entries(blockRecords)) {
    if (!lacksOwnHandle(record.handle)) {
      ctx.claim(record.handle!);
      continue;
    }
    blockRecords[name] = ownRecord(record);
    changed = true;
  }
  for (const [name, block] of Object.entries(blocks)) {
    if (isLayoutBlockName(name)) {
      // Reserved layout blocks are skeleton / import owned; their children are
      // emitted from the paired SPACE. LAYOUT NEW blocks are minted in section 4.
      if (!lacksOwnHandle(block.handle)) ctx.claim(block.handle);
      if (!lacksOwnHandle(block.endblkHandle)) ctx.claim(block.endblkHandle!);
      continue;
    }
    const isNew = lacksOwnHandle(block.handle);
    if (!isNew) {
      ctx.claim(block.handle);
      if (!lacksOwnHandle(block.endblkHandle)) ctx.claim(block.endblkHandle!);
    }
    let record = blockRecords[name];
    if (record === undefined) {
      // The typed default record — the shape the BLOCK command creates.
      record = ownRecord(blockRecordSchema.parse({ name }));
      blockRecords[name] = record;
      changed = true;
    }
    const next: BlockDefinition = { ...block };
    let blockChanged = false;
    if (isNew) {
      next.handle = ctx.mint();
      newBlockNames.add(name);
      blockChanged = true;
    }
    if (lacksOwnHandle(next.endblkHandle)) {
      next.endblkHandle = ctx.mint();
      blockChanged = true;
    }
    if (isNew || lacksOwnHandle(next.ownerHandle)) {
      next.ownerHandle = record.handle;
      blockChanged = true;
    }
    if (blockChanged) {
      blocks[name] = next;
      changed = true;
    }
  }
  return {
    blocks: changed ? blocks : document.blocks,
    blockRecords: changed ? blockRecords : document.blockRecords,
    newBlockNames,
  };
}

/* ── 5. entities ─────────────────────────────────────────────────────────── */

/** Structural view of the entity fields this section touches. */
interface EntityLike {
  id: string;
  type?: string;
  /** An ATTDEF block child's own group-5 (emitted instead of `id`). */
  handle?: string;
  ownerHandle?: string;
  /** A DIMENSION's `*D<n>` block reference (group 2). */
  anonymousBlockName?: string;
  attributes?: Array<{ handle: string }>;
  /** A complex entity's terminating SEQEND own group-5 (INSERT / heavy POLYLINE). */
  seqendHandle?: string;
}

/**
 * Rewrite one entity list (a space's entities or a block's children) for
 * export: mint non-hex ids, follow sibling refs, wire owners, de-duplicate the
 * INSERT sequence handles. Returns the same array when nothing changed.
 */
function prepareEntityList<T extends { id: string }>(
  entities: readonly T[],
  ownerHandle: string | undefined,
  /**
   * `"all"` — every child takes `ownerHandle` (a NEW block: the cloned
   * children carry the stale space owner); `"missing"` — an entity without an
   * owner gets it (top-level authored entities); `"minted"` — only an entity
   * whose id was minted here (a child authored inside an EXISTING imported
   * block — the imported children never carried a typed owner and are
   * emitted as before).
   */
  ownerPolicy: "all" | "missing" | "minted",
  ctx: PreparationContext,
  /** The per-occurrence `*D<n>` names of DIMENSIONs created after import. */
  dimBlocks: Pick<AnonymousDimensionBlockAssignment, "nameByEntity">,
): T[] {
  // PASS 1 — own ids: a valid hex id claims itself (first occurrence; a later
  // duplicate — a RETAIN clone with the same id — is reminted), anything else
  // is minted. The scope map carries old → final for the ref rewrite below;
  // kept ids map to themselves so a strict-resolve sibling ref (HATCH boundary)
  // still resolves.
  const finalIds: string[] = [];
  const scope = new Map<string, string>();
  for (const entity of entities) {
    const source = entity as EntityLike;
    const current = source.id;
    if (source.type === "Attdef") {
      // An ATTDEF emits its group-5 from `handle` (`drawingAttdefToDxfAttdef`),
      // not from `id`: the walker below claims / mints that own handle (an
      // absent one is minted, a unique one kept). The id is a model-level key.
      finalIds.push(current);
      if (typeof source.handle === "string" && isHexHandle(source.handle)) {
        scope.set(source.handle.toLowerCase(), source.handle);
      }
      continue;
    }
    const keep = !lacksOwnHandle(current) && ctx.claim(current);
    const final = keep ? current : ctx.mint();
    finalIds.push(final);
    if (current.length > 0 && !scope.has(current.toLowerCase())) {
      scope.set(current.toLowerCase(), final);
    }
  }
  // PASS 2 — clone + rewrite. An entity that keeps its id, already has its
  // owner (or none is known), carries only hex handles that map to themselves
  // and owns no nested records (no ATTRIB sequence, not an ATTDEF) cannot be
  // changed by the walker — it is kept BY REFERENCE (no clone), which keeps
  // an untouched imported document identical and the pass cheap.
  let changed = false;
  const next = entities.map((entity, index) => {
    const source = entity as unknown as EntityLike;
    const final = finalIds[index]!;
    const idMinted = source.id !== final;
    // The OWN identity the file carries: the `id` for every entity except an
    // ATTDEF, whose group-5 is its `handle` (minted by the walker below when
    // absent). A record whose own identity was minted here was CREATED after
    // import — it is owner-worthy under the `"minted"` policy too.
    const ownIdentityMinted =
      idMinted || (source.type === "Attdef" && lacksOwnHandle(source.handle));
    const wireOwner =
      ownerHandle !== undefined &&
      (ownerPolicy === "all" ||
        (ownerPolicy === "missing" && lacksOwnHandle(source.ownerHandle)) ||
        (ownerPolicy === "minted" && ownIdentityMinted));
    // A DIMENSION created after import takes its allocated `*D<n>` block
    // (per occurrence, in visit order — the same queue the skeleton consumes).
    const anonymousName = takeAnonymousDimensionBlockName(dimBlocks, entity as object);
    if (
      !idMinted &&
      !wireOwner &&
      anonymousName === undefined &&
      !ownsNestedRecords(source) &&
      !mayNeedReferenceRewrite(entity, scope)
    ) {
      return entity;
    }
    const clone = structuredClone(entity) as unknown as EntityLike;
    if (idMinted) clone.id = final;
    if (wireOwner) clone.ownerHandle = ownerHandle;
    if (anonymousName !== undefined) clone.anonymousBlockName = anonymousName;
    // Sibling refs + nested own handles (ATTRIB `attributes[].handle`, ATTDEF
    // `handle`) through the skeleton's generic walker: an absent / claimed
    // nested own handle is reminted, refs follow the scope map.
    rewriteEntityHandlesGeneric(clone, scope, ctx.walkerMinter);
    // The terminating SEQEND's own handle (not a nested `handle` key — the
    // walker leaves it to the caller): absent, non-hex or already claimed →
    // fresh; kept ones claim themselves. WHICH entities emit a SEQEND is
    // {@link emitsTerminatingSeqend}'s single declaration (@codex PR #785 round
    // 3, P1) — an INSERT only when it carries attributes, a heavy POLYLINE
    // always — so this guard, the from-scratch skeleton and the reducer clone
    // helper read ONE list instead of three inline type tests that drifted.
    if (emitsTerminatingSeqend(clone)) {
      if (lacksOwnHandle(clone.seqendHandle) || !ctx.claim(clone.seqendHandle!)) {
        clone.seqendHandle = ctx.mint();
      }
    }
    if (entityEquals(source, clone)) return entity;
    changed = true;
    return clone as unknown as T;
  });
  return changed ? next : (entities as T[]);
}

/** Structural equality of the export-relevant clone against its source. */
function entityEquals(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Does `entity` own nested records the walker must visit — an ATTDEF's own
 * group-5, an INSERT's ATTRIB children, a heavy POLYLINE's VERTEX records, or a
 * terminating SEQEND?
 *
 * @codex PR #785 round 3 (P1) — this used to be a TYPE LIST naming `Attdef` and
 * `Block`, so an imported heavy POLYLINE (whose VERTEX records each carry a
 * group-5) was passed through BY REFERENCE: its child handles were never
 * CLAIMED, and a clone that spread those same handles therefore claimed them
 * FIRST and kept them verbatim → two VERTEX records with one group-5 in the
 * exported file. {@link ownsNestedRecordIdentity} answers the question
 * STRUCTURALLY (any nested own group-5 key), so the next complex entity is
 * covered without being remembered; {@link emitsTerminatingSeqend} adds the one
 * case structure cannot see — a POLYLINE authored after import owns a SEQEND
 * that has no handle YET and must be minted one.
 *
 * The `"Attdef"` test is kept as-is rather than folded into the structural
 * query: an ATTDEF's own group-5 is its `handle` (not its `id`), which the two
 * sites above already encode by type (`finalIds` PASS 1 and
 * `ownIdentityMinted`), and an ATTDEF that reached here WITHOUT the key at all
 * would otherwise stop being visited — a silent narrowing of the pre-existing
 * guarantee. Same list, one more reader, no new one.
 */
function ownsNestedRecords(entity: EntityLike): boolean {
  if (entity.type === "Attdef") return true;
  return ownsNestedRecordIdentity(entity) || emitsTerminatingSeqend(entity);
}

/**
 * Could the generic walker change a reference on `entity`? Only when some
 * harvested handle is not a hex handle (dropped / mapped) or is a scope key
 * whose final handle differs (a remapped sibling).
 */
function mayNeedReferenceRewrite(entity: unknown, scope: ReadonlyMap<string, string>): boolean {
  const harvested = new Set<string>();
  collectHandlesDeep(entity, harvested);
  for (const h of harvested) {
    if (!isHexHandle(h)) return true;
    const mapped = scope.get(h);
    if (mapped !== undefined && mapped.toLowerCase() !== h) return true;
  }
  return false;
}

/* ── orchestrator ────────────────────────────────────────────────────────── */

/**
 * Prepare an IMPORTED document for export (see the module doc). Returns the
 * input by reference when the document carries no `$HANDSEED` handle space or
 * nothing needed a handle / owner; otherwise a derived document (the input is
 * never mutated).
 */
export function prepareImportedDocumentForExport(
  document: DrawingDocument,
  options: {
    /** The LTYPE names the export emits (reference-filtered); all when absent. */
    referencedLinetypeNames?: ReadonlySet<string>;
  } = {},
): DrawingDocument {
  const seed = documentHandSeed(document);
  if (seed === undefined || !isHexHandle(seed)) return document;
  const ctx = createPreparationContext(document, seed);
  claimRawOwnHandles(document, ctx);

  // 2. symbol-table records.
  const styleTables = prepareStyleTables(document, ctx);
  const linetypes = prepareLinetypes(document, ctx, options.referencedLinetypeNames);

  // 3. blocks + block records — a DIMENSION created after import first gets
  //    its anonymous `*D<n>` BLOCK + BLOCK_RECORD in the typed model (the same
  //    assignment the skeleton uses), so `prepareBlocks` mints / owns them like
  //    any new block and the writer never falls back to its record-less stub.
  const dimBlocks = assignAnonymousDimensionBlocks(document, document.blocks);
  const withDimBlocks =
    Object.keys(dimBlocks.blocks).length === 0
      ? document
      : {
          ...document,
          blocks: { ...document.blocks, ...dimBlocks.blocks },
          blockRecords: { ...document.blockRecords, ...dimBlocks.blockRecords },
        };
  const prepared = prepareBlocks(withDimBlocks, ctx);

  // 4. layouts (LAYOUT NEW) — may add a `*Paper_Space<N>` record + block.
  const layouts = prepareImportedLayouts(
    { ...withDimBlocks, blocks: prepared.blocks, blockRecords: prepared.blockRecords },
    ctx,
  );
  const blocks = layouts.blocks;
  const blockRecords = layouts.blockRecords;

  // 5. entities — spaces, then the block children the export emits.
  let spacesChanged = false;
  const spaces = document.spaces.map((space) => {
    if (space.kind === "block-test" || space.kind === "block-edit") return space;
    const entities = prepareEntityList(
      space.entities,
      layouts.ownerBySpaceId.get(space.id),
      "missing",
      ctx,
      dimBlocks,
    );
    if (entities === space.entities) return space;
    spacesChanged = true;
    return { ...space, entities };
  });
  const emitted = new Map(emittedBlockEntries({ ...document, blocks }));
  let blocksChanged = blocks !== document.blocks;
  const finalBlocks: Record<string, BlockDefinition> = { ...blocks };
  for (const [name, block] of emitted) {
    const record = blockRecords[name];
    const entities = prepareEntityList(
      block.entities,
      record?.handle && isHexHandle(record.handle) ? record.handle : undefined,
      prepared.newBlockNames.has(name) ? "all" : "minted",
      ctx,
      dimBlocks,
    );
    if (entities === block.entities) continue;
    finalBlocks[name] = { ...block, entities };
    blocksChanged = true;
  }

  const changed =
    styleTables !== document.styleTables ||
    linetypes !== document.linetypes ||
    blocksChanged ||
    blockRecords !== document.blockRecords ||
    layouts.layouts !== document.layouts ||
    layouts.rawObjectGroups !== document.rawObjectGroups ||
    spacesChanged;
  if (!changed) return document;
  return {
    ...document,
    styleTables,
    ...(linetypes !== undefined ? { linetypes } : {}),
    blocks: blocksChanged ? finalBlocks : document.blocks,
    blockRecords,
    layouts: layouts.layouts,
    layoutsBySpaceId: layouts.layoutsBySpaceId,
    ...(layouts.rawObjectGroups !== undefined ? { rawObjectGroups: layouts.rawObjectGroups } : {}),
    spaces: spacesChanged ? spaces : document.spaces,
  };
}
